<template>
  <div ref="rootElement" :class="style.floatingRoot">
    <slot
      name="trigger"
      :isOpen="isOpen"
      :open="open"
      :close="close"
      :toggle="toggle"
    />

    <Teleport v-if="teleport" :to="teleportTo">
      <div
        v-if="isOpen"
        ref="popupElement"
        data-floating-popup
        :class="[style.floatingPopup, popupClass]"
        :style="computedPopupStyle"
        @click.stop
      >
        <slot :close="close" />
      </div>
    </Teleport>

    <div
      v-else-if="isOpen"
      ref="popupElement"
      data-floating-popup
      :class="[style.floatingPopup, popupClass]"
      :style="computedPopupStyle"
      @click.stop
    >
      <slot :close="close" />
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent, PropType, nextTick} from "vue";
import style from "./FloatingPopup.module.scss";

export default defineComponent({
  name: "FloatingPopup",

  emits: {
    "update:modelValue": (value: boolean): boolean => {
      return typeof value === "boolean";
    },

    open: (): boolean => true,

    close: (): boolean => true
  },

  props: {
    /** Управляет открытым/закрытым состоянием попапа через v-model. */
    modelValue: {
      type: Boolean,
      default: false
    },

    /** Переносит попап через Teleport, чтобы он не обрезался родителями с overflow. */
    teleport: {
      type: Boolean,
      default: true
    },

    /** CSS-селектор контейнера, куда будет перенесен попап при teleport === true. */
    teleportTo: {
      type: String,
      default: "body"
    },

    /** Положение попапа относительно trigger-элемента. */
    placement: {
      type: String as PropType<PopupPlacement>,
      default: "bottom-start"
    },

    /** Отступ в пикселях между trigger-элементом и попапом. */
    offset: {
      type: Number,
      default: 8
    },

    /** Фиксированная ширина попапа: число преобразуется в px, строка используется как есть. */
    width: {
      type: [String, Number] as PropType<string | number>,
      default: ""
    },

    /** Делает минимальную ширину попапа равной ширине trigger-элемента. */
    minWidthByTrigger: {
      type: Boolean,
      default: false
    },

    /** Событие: Закрывает попап при клике вне trigger-элемента и вне самого попапа. */
    closeOnOutsideClick: {
      type: Boolean,
      default: true
    },

    /** Событие: Закрывает попап при нажатии клавиши Escape. */
    closeOnEscape: {
      type: Boolean,
      default: true
    },

    /** Событие: Закрывает попап при скролле; если false — пересчитывает позицию. */
    closeOnScroll: {
      type: Boolean,
      default: true
    },

    /** Событие: Закрывает попап при изменении размера окна; если false — пересчитывает позицию. */
    closeOnResize: {
      type: Boolean,
      default: true
    },

    /** Дополнительный класс или набор классов для элемента попапа. */
    popupClass: {
      type: [String, Array, Object],
      default: ""
    },

    /** Дополнительные inline-стили попапа, применяются поверх вычисленных стилей. */
    popupStyle: {
      type: Object as PropType<Record<string, string | number>>,
      default: (): Record<string, string | number> => ({})
    }
  },

  data() {
    return {
      style,
      anchorElement: null as HTMLElement | null,
      isPositionReady: false,
      position: {
        top: 0,
        left: 0
      } as PopupPosition,
      triggerWidth: 0
    };
  },

  computed: {
    isOpen(): boolean {
      return this.modelValue;
    },

    normalizedWidth(): string | undefined {
      if (this.width === "") {
        return undefined;
      }

      if (typeof this.width === "number") {
        return `${this.width}px`;
      }

      return this.width;
    },

    computedPopupStyle(): Record<string, string | number> {
      const baseStyle: Record<string, string | number> = {
        position: this.teleport ? "fixed" : "absolute",
        top: `${this.position.top}px`,
        left: `${this.position.left}px`,
        zIndex: 10000,
        visibility: this.isPositionReady ? "visible" : "hidden"
      };

      if (this.normalizedWidth) {
        baseStyle.width = this.normalizedWidth;
      }

      if (this.minWidthByTrigger && this.triggerWidth > 0) {
        baseStyle.minWidth = `${this.triggerWidth}px`;
      }

      return {
        ...baseStyle,
        ...this.popupStyle
      };
    }
  },

  mounted() {
    document.addEventListener("pointerdown", this.handleDocumentPointerDown);
    document.addEventListener("keydown", this.handleKeydown);
    window.addEventListener("resize", this.handleResize);
    window.addEventListener("scroll", this.handleScroll, true);

    if (this.modelValue) {
      this.open();
    }
  },

  beforeUnmount() {
    document.removeEventListener("pointerdown", this.handleDocumentPointerDown);
    document.removeEventListener("keydown", this.handleKeydown);
    window.removeEventListener("resize", this.handleResize);
    window.removeEventListener("scroll", this.handleScroll, true);
  },

  methods: {
    async open(event?: MouseEvent): Promise<void> {
      this.anchorElement = this.resolveAnchorElement(event);
      this.isPositionReady = false;

      this.$emit("update:modelValue", true);
      this.$emit("open");

      await nextTick();
      await this.waitAnimationFrame();

      this.updatePosition();
      this.isPositionReady = true;
    },

    close(): void {
      if (!this.isOpen) {
        return;
      }

      this.isPositionReady = false;
      this.$emit("update:modelValue", false);
      this.$emit("close");
    },

    toggle(event?: MouseEvent): void {
      if (this.isOpen) {
        this.close();
        return;
      }

      this.open(event);
    },

    waitAnimationFrame(): Promise<void> {
      return new Promise((resolve) => {
        window.requestAnimationFrame(() => {
          resolve();
        });
      });
    },

    resolveAnchorElement(event?: MouseEvent): HTMLElement | null {
      if (event?.currentTarget instanceof HTMLElement) {
        return event.currentTarget;
      }

      if (event?.target instanceof HTMLElement) {
        const closestInteractiveElement = event.target.closest(
          "button, [role='button'], a, input, textarea, select, [data-floating-trigger]"
        );

        if (closestInteractiveElement instanceof HTMLElement) {
          return closestInteractiveElement;
        }

        return event.target;
      }

      const rootElement = this.$refs.rootElement as HTMLElement | undefined;

      if (!rootElement) {
        return null;
      }

      const firstInteractiveElement = rootElement.querySelector(
        "button, [role='button'], a, input, textarea, select, [data-floating-trigger]"
      );

      if (firstInteractiveElement instanceof HTMLElement) {
        return firstInteractiveElement;
      }

      const firstElementChild = rootElement.firstElementChild;

      if (firstElementChild instanceof HTMLElement) {
        return firstElementChild;
      }

      return rootElement;
    },

    getAnchorRect(): DOMRect | null {
      if (this.anchorElement) {
        return this.anchorElement.getBoundingClientRect();
      }

      const fallbackAnchor = this.resolveAnchorElement();

      if (fallbackAnchor) {
        this.anchorElement = fallbackAnchor;
        return fallbackAnchor.getBoundingClientRect();
      }

      return null;
    },

    getLocalOffset(): PopupPosition {
      if (this.teleport) {
        return {
          top: 0,
          left: 0
        };
      }

      const popupElement = this.$refs.popupElement as HTMLElement | undefined;
      const offsetParent = popupElement?.offsetParent;

      if (offsetParent instanceof HTMLElement) {
        const rect = offsetParent.getBoundingClientRect();

        return {
          top: rect.top,
          left: rect.left
        };
      }

      return {
        top: 0,
        left: 0
      };
    },

    updatePosition(): void {
      const anchorRect = this.getAnchorRect();
      const popupElement = this.$refs.popupElement as HTMLElement | undefined;

      if (!anchorRect || !popupElement) {
        return;
      }

      const popupRect = popupElement.getBoundingClientRect();
      const margin = 12;
      const localOffset = this.getLocalOffset();

      this.triggerWidth = anchorRect.width;

      let top = anchorRect.bottom + this.offset;
      let left = anchorRect.left;

      if (this.placement === "bottom") {
        top = anchorRect.bottom + this.offset;
        left = anchorRect.left + (anchorRect.width - popupRect.width) / 2;
      }

      if (this.placement === "bottom-start") {
        top = anchorRect.bottom + this.offset;
        left = anchorRect.left;
      }

      if (this.placement === "bottom-end") {
        top = anchorRect.bottom + this.offset;
        left = anchorRect.right - popupRect.width;
      }

      if (this.placement === "top") {
        top = anchorRect.top - popupRect.height - this.offset;
        left = anchorRect.left + (anchorRect.width - popupRect.width) / 2;
      }

      if (this.placement === "top-start") {
        top = anchorRect.top - popupRect.height - this.offset;
        left = anchorRect.left;
      }

      if (this.placement === "top-end") {
        top = anchorRect.top - popupRect.height - this.offset;
        left = anchorRect.right - popupRect.width;
      }

      if (this.placement === "left") {
        top = anchorRect.top + (anchorRect.height - popupRect.height) / 2;
        left = anchorRect.left - popupRect.width - this.offset;
      }

      if (this.placement === "left-start") {
        top = anchorRect.top;
        left = anchorRect.left - popupRect.width - this.offset;
      }

      if (this.placement === "left-end") {
        top = anchorRect.bottom - popupRect.height;
        left = anchorRect.left - popupRect.width - this.offset;
      }

      if (this.placement === "right") {
        top = anchorRect.top + (anchorRect.height - popupRect.height) / 2;
        left = anchorRect.right + this.offset;
      }

      if (this.placement === "right-start") {
        top = anchorRect.top;
        left = anchorRect.right + this.offset;
      }

      if (this.placement === "right-end") {
        top = anchorRect.bottom - popupRect.height;
        left = anchorRect.right + this.offset;
      }

      if (left < margin) {
        left = margin;
      }

      if (left + popupRect.width > window.innerWidth - margin) {
        left = window.innerWidth - popupRect.width - margin;
      }

      if (top < margin) {
        top = margin;
      }

      if (top + popupRect.height > window.innerHeight - margin) {
        top = window.innerHeight - popupRect.height - margin;
      }

      this.position = {
        top: top - localOffset.top,
        left: left - localOffset.left
      };
    },

    handleDocumentPointerDown(event: PointerEvent): void {
      if (!this.isOpen || !this.closeOnOutsideClick) {
        return;
      }

      const rootElement = this.$refs.rootElement as HTMLElement | undefined;
      const popupElement = this.$refs.popupElement as HTMLElement | undefined;
      const target = event.target as Node | null;

      if (!target) {
        return;
      }

      if (rootElement?.contains(target)) {
        return;
      }

      if (popupElement?.contains(target)) {
        return;
      }

      this.close();
    },

    handleKeydown(event: KeyboardEvent): void {
      if (!this.isOpen || !this.closeOnEscape) {
        return;
      }

      if (event.key === "Escape") {
        this.close();
      }
    },

    async handleResize(): Promise<void> {
      if (!this.isOpen) {
        return;
      }

      if (this.closeOnResize) {
        this.close();
        return;
      }

      this.isPositionReady = false;

      await nextTick();
      await this.waitAnimationFrame();

      this.updatePosition();
      this.isPositionReady = true;
    },

    async handleScroll(): Promise<void> {
      if (!this.isOpen) {
        return;
      }

      if (this.closeOnScroll) {
        this.close();
        return;
      }

      this.isPositionReady = false;

      await nextTick();
      await this.waitAnimationFrame();

      this.updatePosition();
      this.isPositionReady = true;
    }
  },

  watch: {
    modelValue(value: boolean): void {
      if (value) {
        this.open();
        return;
      }

      this.isPositionReady = false;
    }
  }
});
</script>
